import { Fragment, useState } from "react";
import { Building2, ChevronDown, ChevronRight, FileSearch, MapPin, Milestone, Ruler } from "lucide-react";
import type { LandDataLevel, LandDataRoad, LandDataView } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useEvidence } from "./evidence";
import { AssetImage, Flag, SectionCard, SourceChip, StatTile, fmtCoord, fmtNum, fmtPct } from "./shared";

/* -------------------------------------------------------------------------- */
/* 区位                                                                         */
/* -------------------------------------------------------------------------- */

export function LocationSection({ data }: { data: LandDataView }): JSX.Element | null {
  const evidence = useEvidence();
  const { dims, maps } = data.location;
  if (!dims.length && !maps.length) return null;
  return (
    <SectionCard
      icon={<MapPin className="h-4 w-4" />}
      id="sec-location"
      title="区位维度"
      count={dims.length ? `${dims.length} 项` : undefined}
      hint={
        data.project.lat != null && data.project.lng != null
          ? `地块坐标 ${fmtCoord(data.project.lat)}, ${fmtCoord(data.project.lng)}`
          : undefined
      }
    >
      {maps.length ? (
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          {maps.map((m) => (
            <figure key={m.asset} className="overflow-hidden rounded-lg border border-cardline">
              <AssetImage convId={data.conv_id} asset={m.asset} alt={m.label} className="h-56 w-full" />
              <figcaption className="border-t border-cardline bg-cardhead px-3 py-1.5 text-[12px] text-zinc-500">
                {m.label}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : null}
      <dl className="divide-y divide-cardline/70 rounded-lg border border-cardline">
        {dims.map((dim, i) => (
          <div key={`${dim.field}-${i}`} className="grid gap-x-4 gap-y-1 px-4 py-2.5 sm:grid-cols-[112px_minmax(0,1fr)_auto]">
            <dt className="flex items-start gap-1.5 text-[13px] font-medium text-brand-900">
              {dim.field}
            </dt>
            <dd className="min-w-0 text-[13px] leading-6 text-zinc-800">
              <span className="whitespace-pre-wrap">{dim.value || "—"}</span>
              {dim.basis ? <div className="text-[12px] leading-5 text-zinc-400">口径：{dim.basis}</div> : null}
            </dd>
            <dd className="flex items-start gap-1.5 sm:justify-end">
              <Flag level={dim.level} withText />
              {dim.url || dim.screenshot || dim.source ? (
                <button
                  type="button"
                  onClick={() =>
                    evidence.open({
                      title: `区位 · ${dim.field}`,
                      value: dim.value.length > 24 ? `${dim.value.slice(0, 24)}…` : dim.value,
                      level: dim.level,
                      quote: dim.value,
                      basis: [dim.basis, dim.confidence ? `置信度 ${dim.confidence}` : "", dim.grade ? `来源等级 ${dim.grade}` : ""],
                      source: { label: dim.source || dim.domain, url: dim.url, domain: dim.domain },
                      asset: dim.screenshot ? { convId: data.conv_id, name: dim.screenshot } : null,
                    })
                  }
                  className="inline-flex items-center gap-1 rounded-full border border-cardline bg-white px-2 py-0.5 text-[11px] text-brand-700 hover:border-brand-300 hover:bg-brand-50"
                >
                  <FileSearch className="h-3 w-3" />
                  证据
                </button>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* 道路                                                                         */
/* -------------------------------------------------------------------------- */

function roadLevel(road: LandDataRoad): LandDataLevel {
  if (road.level) return road.level;
  if (road.status === "verified") return { level: "verified", note: "" };
  if (road.status === "conflict" || road.status === "candidate") return { level: "review", note: "" };
  return { level: "pending", note: "未核到车道数" };
}

function RoadCard({ convId, road }: { convId: string; road: LandDataRoad }): JSX.Element {
  const evidence = useEvidence();
  const level = roadLevel(road);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-cardline bg-canvas p-3 sm:flex-row">
      {road.tile ? (
        <AssetImage
          convId={convId}
          asset={road.tile}
          alt={`${road.road} 卫星图（以路为中心）`}
          className="aspect-[16/10] w-full shrink-0 border border-cardline sm:w-60"
        />
      ) : (
        <div className="flex aspect-[16/10] w-full shrink-0 items-center justify-center rounded-md border border-dashed border-cardline text-[12px] text-zinc-400 sm:w-60">
          无卫星图
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {road.side ? (
            <span className="rounded bg-brand-900 px-1.5 py-px text-[11px] font-medium text-white">{road.side}</span>
          ) : null}
          <span className="text-[15px] font-semibold text-zinc-900">{road.road || "—"}</span>
          <Flag level={level} withText />
        </div>
        <p className="mt-1.5 text-[14px] font-medium leading-6 text-zinc-800">
          {road.fact ||
            (road.status === "conflict"
              ? "多源证据不一致，未下结论"
              : "车道数未核到")}
        </p>
        {level.level !== "verified" && level.note ? (
          <p className="text-[12px] leading-5 text-zinc-500">{level.note}，请对照卫星图自行判读。</p>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-zinc-500">
          {road.distance_m != null ? <span>距地块 {fmtNum(road.distance_m, 0)} 米</span> : null}
          <button
            type="button"
            onClick={() =>
              evidence.open({
                title: `道路 · ${road.side ? `${road.side} · ` : ""}${road.road}`,
                value: road.fact || "未核到",
                level,
                basis: [road.distance_m != null ? `距地块 ${fmtNum(road.distance_m, 0)} 米` : ""],
                links: road.references.map((r) => ({
                  title: r.title || r.provider,
                  url: r.url,
                  domain: r.domain,
                  fact: [r.fact, r.temporal ? `（${r.temporal}）` : ""].join(""),
                  official: r.official,
                })),
                asset: road.tile ? { convId, name: road.tile } : null,
              })
            }
            className="inline-flex items-center gap-1 rounded-full border border-cardline bg-white px-2 py-0.5 text-[11px] text-brand-700 hover:border-brand-300 hover:bg-brand-50"
          >
            <FileSearch className="h-3 w-3" />
            {road.references.length ? `${road.references.length} 条来源` : "证据"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function RoadsSection({ data }: { data: LandDataView }): JSX.Element | null {
  const roads = data.roads.roads;
  if (!roads.length) return null;
  return (
    <SectionCard
      icon={<Milestone className="h-4 w-4" />}
      id="sec-roads"
      title="四至道路车道"
      count={`${roads.length} 条`}
      hint="卫星图以路为中心，点击放大"
    >
      <div className="grid gap-3 xl:grid-cols-2">
        {roads.map((road, i) => (
          <RoadCard key={`${road.road}-${i}`} convId={data.conv_id} road={road} />
        ))}
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* 企业                                                                         */
/* -------------------------------------------------------------------------- */

export function EnterpriseSection({ data }: { data: LandDataView }): JSX.Element | null {
  const ent = data.enterprise;
  // 可入园分行业全部展示、不隐藏: 有子类的行业默认展开(仍可点击收起)。
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set((ent?.industries ?? []).filter((r) => r.children.length > 0).map((r) => r.name)),
  );
  if (!ent) return null;
  const isQcc = ent.provider === "qcc";
  const toggle = (name: string): void =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const industries = ent.industries;
  const maxCount = Math.max(1, ...ent.industries.map((r) => r.count));
  const sumCount = ent.industries.reduce((s, r) => s + r.count, 0);
  return (
    <SectionCard
      icon={<Building2 className="h-4 w-4" />}
      id="sec-enterprise"
      title={isQcc ? "框选范围企业（企查查）" : `周边企业（${fmtNum(ent.radius_km)}km）`}
      count={ent.total != null ? `${isQcc ? "框选" : "可入园"} ${fmtNum(ent.total, 0)} 家` : undefined}
      hint={<Flag level={ent.level} withText />}
      actions={<SourceChip src={{ label: ent.source, url: ent.source_url, domain: "" }} />}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        {isQcc ? (
          <>
            <StatTile label="框选范围内主体总数" value={fmtNum(ent.total, 0)} sub="用户自定义多边形范围 · 含个体工商户" />
            <StatTile label="其中在营 / 存续" value={fmtNum(ent.active_total, 0)} sub="剔除注销 / 吊销" />
            <StatTile label="行业门类数" value={fmtNum(ent.industries.length, 0)} />
          </>
        ) : (
          <>
            <StatTile label="可入园客户总量" value={fmtNum(ent.total, 0)} sub="标准园区准入行业口径 · 不含个体工商户" />
            <StatTile
              label="全行业主体总数"
              value={`${ent.all_enterprises_capped ? "≥ " : ""}${fmtNum(ent.all_enterprises, 0)}`}
              sub={ent.all_enterprises_capped ? "达平台显示上限，实际 ≥ 此数（含个体）" : "含个体工商户"}
              level={ent.all_enterprises_capped ? { level: "review", note: "达平台显示上限" } : null}
            />
            <StatTile label="其中个体工商户" value={fmtNum(ent.individual_biz_count, 0)} sub="全行业主体总数中的个体户" />
          </>
        )}
      </div>
      {!isQcc && ent.center.lat != null ? (
        <p className="mt-2 text-[12px] text-zinc-400">
          检索中心 {fmtCoord(ent.center.lat)}, {fmtCoord(ent.center.lng)}（{ent.center.coord_system}）
        </p>
      ) : null}
      {ent.industries.length ? (
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="text-[13px] font-medium text-zinc-700">
              {isQcc ? "分行业门类" : "可入园客户 · 分行业"}
              <span className="ml-2 text-[11px] font-normal text-zinc-400">占比为计算值 = 该行业 ÷ 各行业合计 {fmtNum(sumCount, 0)}</span>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-cardline">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-cardhead text-[12px] text-zinc-500">
                  <th className="px-3 py-1.5 text-left font-medium">行业</th>
                  <th className="w-[38%] px-3 py-1.5 font-medium" />
                  <th className="px-3 py-1.5 text-right font-medium">家数</th>
                  <th className="px-3 py-1.5 text-right font-medium">占比</th>
                </tr>
              </thead>
              <tbody>
                {industries.map((row, i) => {
                  const hasChildren = row.children.length > 0;
                  const open = hasChildren && expanded.has(row.name);
                  return (
                    <Fragment key={row.name}>
                      <tr className={cn("border-t border-cardline/70", i % 2 ? "bg-canvas/60" : "bg-white")}>
                        <td className="px-3 py-1.5 text-zinc-700">
                          {hasChildren ? (
                            <button
                              type="button"
                              onClick={() => toggle(row.name)}
                              className="flex items-center gap-1 text-left hover:text-brand-700"
                              aria-expanded={open}
                            >
                              {open ? <ChevronDown className="h-3.5 w-3.5 text-zinc-400" /> : <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />}
                              {row.name}
                              <span className="text-[11px] text-zinc-400">{row.children.length} 子类</span>
                            </button>
                          ) : (
                            row.name
                          )}
                        </td>
                        <td className="px-3 py-1.5">
                          <span className="block h-2.5 rounded-sm bg-zinc-100">
                            <span className="block h-2.5 rounded-sm bg-brand-300" style={{ width: `${(row.count / maxCount) * 100}%` }} />
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right font-medium tabular-nums text-zinc-900">{fmtNum(row.count, 0)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500">{sumCount ? fmtPct(row.count / sumCount) : "—"}</td>
                      </tr>
                      {open
                        ? row.children.map((child) => (
                            <tr key={`${row.name}-${child.code || child.name}`} className="border-t border-cardline/40 bg-zinc-50/70">
                              <td className="py-1 pl-9 pr-3 text-[12px] text-zinc-600">└ {child.name}</td>
                              <td className="px-3 py-1">
                                <span className="block h-2 rounded-sm bg-zinc-100">
                                  <span className="block h-2 rounded-sm bg-brand-200" style={{ width: `${(child.count / maxCount) * 100}%` }} />
                                </span>
                              </td>
                              <td className="px-3 py-1 text-right text-[12px] tabular-nums text-zinc-700">{fmtNum(child.count, 0)}</td>
                              <td />
                            </tr>
                          ))
                        : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* 土地                                                                         */
/* -------------------------------------------------------------------------- */

export function LandSupplySection({ data }: { data: LandDataView }): JSX.Element | null {
  const land = data.land_supply;
  if (!land) return null;
  const rows = [...land.districts].sort((a, b) => Number(!!b.is_target) - Number(!!a.is_target));
  return (
    <SectionCard
      icon={<Ruler className="h-4 w-4" />}
      id="sec-land"
      title="工业用地成交（国信网）"
      count={land.parcels != null ? `${fmtNum(land.parcels, 0)} 宗` : undefined}
      hint={
        <span className="inline-flex items-center gap-2">
          {land.window ? `出让 ${land.window} · ${land.region_level === "city" ? "城市级" : "省级近似"}` : null}
          <Flag level={land.level} withText />
        </span>
      }
      actions={<SourceChip src={{ label: "国信网 CREI", url: land.source_url, domain: "" }} />}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="工业仓储成交宗地" value={fmtNum(land.parcels, 0)} sub={land.region} />
        <StatTile label="30亩以下" value={fmtNum(land.under_30mu, 0)} sub={`占比 ${fmtPct(land.share_under_30mu)}`} />
        <StatTile label="15亩以下" value={fmtNum(land.under_15mu, 0)} sub={`占比 ${fmtPct(land.share_under_15mu)}`} />
      </div>
      {rows.length ? (
        <div className="mt-4 overflow-x-auto rounded-lg border border-cardline">
          <table className="w-full min-w-[560px] text-[13px]">
            <thead>
              <tr className="bg-cardhead text-left text-[12px] text-zinc-500">
                <th className="px-3 py-2 font-medium">区县</th>
                <th className="px-3 py-2 text-right font-medium">成交宗数</th>
                <th className="px-3 py-2 text-right font-medium">占全市</th>
                <th className="px-3 py-2 text-right font-medium">30亩以下</th>
                <th className="px-3 py-2 text-right font-medium">占比</th>
                <th className="px-3 py-2 text-right font-medium">15亩以下</th>
                <th className="px-3 py-2 text-right font-medium">占比</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.name}
                  id={row.is_target ? "land-target-row" : undefined}
                  className={cn("border-t border-cardline/70 transition-shadow", row.is_target ? "bg-brand-50" : i % 2 ? "bg-canvas/60" : "bg-white")}
                >
                  <td className={cn("px-3 py-1.5", row.is_target ? "font-semibold text-brand-900" : "text-zinc-700")}>
                    {row.is_target ? <span className="mr-1 text-brand-600">★</span> : null}
                    {row.name}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(row.parcels, 0)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500" title="计算值：本区宗数 ÷ 城市宗数（部分宗地不挂区县，合计可能小于城市数）">
                    {row.parcels != null && land.parcels ? fmtPct(row.parcels / land.parcels) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(row.under_30mu, 0)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500">{fmtPct(row.share_under_30mu)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(row.under_15mu, 0)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500">{fmtPct(row.share_under_15mu)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <p className="mt-3 text-[12px] leading-5 text-zinc-400">
        {land.source}
        {land.note ? ` · ${land.note}` : ""}
        {" · "}「占全市」为计算值，部分宗地不挂区县，各区合计可能小于城市总数。
      </p>
    </SectionCard>
  );
}
